'use client';

import { useEffect, useState } from 'react';

import { Add } from '@depop/web-thrift-ui/Icons/Add';
import { ArrowLeft } from '@depop/web-thrift-ui/Icons/ArrowLeft';
import { BoostOutline } from '@depop/web-thrift-ui/Icons/BoostOutline';
import { Click } from '@depop/web-thrift-ui/Icons/Click';
import { Close } from '@depop/web-thrift-ui/Icons/Close';
import { Copy } from '@depop/web-thrift-ui/Icons/Copy';
import { HomeOutline } from '@depop/web-thrift-ui/Icons/HomeOutline';
import { ID } from '@depop/web-thrift-ui/Icons/ID';
import { Location } from '@depop/web-thrift-ui/Icons/Location';
import { Map } from '@depop/web-thrift-ui/Icons/Map';
import { OutfitOutline } from '@depop/web-thrift-ui/Icons/OutfitOutline';
import { Refresh } from '@depop/web-thrift-ui/Icons/Refresh';
import { Selling } from '@depop/web-thrift-ui/Icons/Selling';
import { Suggestion } from '@depop/web-thrift-ui/Icons/Suggestion';
import { Video } from '@depop/web-thrift-ui/Icons/Video';
import { Caption } from '@depop/web-thrift-ui/Typography';
import { DepopGroupIconIcon } from '@depop/web-ui-kit/Icons/DepopGroupIconIcon';
import dynamic from 'next/dynamic';
import { useSelectedLayoutSegments } from 'next/navigation';

import { ATEvents } from './components/Tools/ATEvents';
import { ExperimentSwitcher } from './components/Tools/ExperimentSwitcher';
import { FigmaCapture } from './components/Tools/FigmaCapture';
import { GeoSwitch } from './components/Tools/GeoSwitch';
import { MagicLinkPaste } from './components/Tools/MagicLinkPaste';
import { PreviewEnv } from './components/Tools/PreviewEnv';
import { ProductInfo } from './components/Tools/ProductInfo';
import { RouteInfo } from './components/Tools/RouteInfo';
import { ShopInfo } from './components/Tools/ShopInfo';
import { SwitchUser } from './components/Tools/SwitchUser';
import { UserInfo } from './components/Tools/UserInfo';
import styles from './styles.module.css';

import { ToolCollection } from '@/components/AdminToolbox/components/ToolCollection';
import { ACTIVE_TOOL_SESSION_STORAGE_KEY } from '@/modules/adminTool/constants';
import { type Tools } from '@/modules/adminTool/types';
import { useDepopChromeExt } from '@/modules/adminTool/useDepopChromeExt';
import { useBoostedListingAccess } from '@/modules/boostedListings/hooks/useBoostedListingAccess';
import { useBulkListingEnabled } from '@/modules/bulkListing/useBulkListing';
import { isDevelopment } from '@/modules/env/isDevelopment';
import { isServerCheck } from '@/modules/env/isServerCheck';
import { isStaging } from '@/modules/env/isStaging';
import {
  getSessionStorageItem,
  removeSessionStorageItem,
  setSessionStorageItem,
} from '@/modules/storage';
import { useCurrentUser } from '@/modules/user/useCurrentUser';

const isNonProd = isDevelopment() || isStaging();

const BulkCreateUsers = isNonProd
  ? dynamic(
      () =>
        import('./components/Tools/BulkCreateUsers').then(
          (mod) => mod.BulkCreateUsers
        ),
      { ssr: false }
    )
  : null;
const BulkCreateProducts = isNonProd
  ? dynamic(
      () =>
        import('./components/Tools/BulkCreateProducts').then(
          (mod) => mod.BulkCreateProducts
        ),
      { ssr: false }
    )
  : null;

function VersionTag() {
  const githubUrl = `https://github.com/depop/web-default-website-server/commit/${process.env.NEXT_PUBLIC_APP_VERSION}`;

  if (process.env.NEXT_PUBLIC_DEPLOY_ENV === 'development') {
    return 'local';
  }

  return (
    <a
      className={styles.versionInformationLink}
      target="_blank"
      href={githubUrl}
    >
      {process.env.NEXT_PUBLIC_APP_VERSION}
    </a>
  );
}

export function AdminToolbox() {
  const lastActiveToolKey = !isServerCheck()
    ? getSessionStorageItem(ACTIVE_TOOL_SESSION_STORAGE_KEY)
    : '';
  const [open, setOpen] = useState(Boolean(lastActiveToolKey));
  const [activeToolKey, setActiveToolKey] = useState<string>(
    lastActiveToolKey || ''
  );
  const [, currentUser] = useCurrentUser();
  const { data: hasBulkListingAccess } = useBulkListingEnabled(currentUser?.id);
  const { hasBoostedListingsAccess } = useBoostedListingAccess();
  const [mounted, setMounted] = useState(false);
  const hasDepopChromeExt = useDepopChromeExt();
  const layoutSegments = useSelectedLayoutSegments();
  const isProductPage = layoutSegments[0] === 'products';
  const isShopPage = layoutSegments?.[1] === '(shop)';

  function isProdEnv() {
    return process.env.NEXT_PUBLIC_DEPLOY_ENV === 'production';
  }

  function toggleAdminToolOpen() {
    setOpen((value) => !value);
  }

  function openTool(toolKey: string) {
    setActiveToolKey(toolKey);
    setSessionStorageItem(ACTIVE_TOOL_SESSION_STORAGE_KEY, toolKey);
  }

  function closeActiveTool() {
    setActiveToolKey('');
    removeSessionStorageItem(ACTIVE_TOOL_SESSION_STORAGE_KEY);
  }

  const PRIMARY_TOOLS: Tools = {
    'AT events': {
      icon: <Click size="md" />,
      component: <ATEvents />,
    },
    Experiments: {
      icon: <BoostOutline size="md" />,
      component: <ExperimentSwitcher />,
    },
    Routing: {
      icon: <Map size="md" />,
      component: <RouteInfo />,
    },
    ...(isNonProd && {
      'Geo switch': {
        icon: <Location size="md" />,
        component: <GeoSwitch />,
      },
    }),
    'Preview env': {
      icon: <Video size="md" />,
      component: <PreviewEnv />,
    },
    'Switch user': {
      icon: <Refresh size="md" />,
      component: <SwitchUser />,
    },
    'Paste your magic link': {
      icon: <Suggestion size="md" />,
      component: <MagicLinkPaste />,
    },
    'Figma capture': {
      icon: <Copy size="md" />,
      component: <FigmaCapture />,
    },
    ...(BulkCreateUsers && {
      'Bulk create users': {
        icon: <Add size="md" border="regular" />,
        component: <BulkCreateUsers />,
      },
    }),
    ...(BulkCreateProducts && {
      'Bulk create products': {
        icon: <OutfitOutline size="md" />,
        component: <BulkCreateProducts />,
      },
    }),
  };

  const USER_TOOLS = {
    'User info': {
      icon: <ID size="md" />,
      component: currentUser && (
        <UserInfo
          user={currentUser}
          hasBulkListingAccess={Boolean(hasBulkListingAccess)}
          hasBoostedListingsAccess={hasBoostedListingsAccess}
        />
      ),
    },
  };

  const PAGE_TOOLS: Tools = {
    ...(isProductPage && {
      Product: {
        icon: <Selling size="md" />,
        component: <ProductInfo />,
      },
    }),
    ...(isShopPage && {
      Shop: {
        icon: <HomeOutline size="md" />,
        component: <ShopInfo />,
      },
    }),
  };

  const TOOLS: Tools = {
    ...PRIMARY_TOOLS,
    ...USER_TOOLS,
    ...PAGE_TOOLS,
  };

  const activeTool = TOOLS[activeToolKey];
  const hasPageTools = Object.keys(PAGE_TOOLS).length > 0;

  useEffect(() => {
    if (!open) {
      closeActiveTool();
    }
  }, [open]);

  useEffect(() => {
    setMounted(true);
  }, []);

  if (mounted && (hasDepopChromeExt || !isProdEnv())) {
    return (
      <>
        <button
          className={styles.toggleButton}
          onClick={toggleAdminToolOpen}
          aria-label={open ? 'close admin tool' : 'open admin tool'}
        >
          {open ? (
            <Close size="md" border="regular" />
          ) : (
            <DepopGroupIconIcon size={32} />
          )}
        </button>
        {open && (
          <div className={styles.modal}>
            {activeTool ? (
              <div>
                <div className={styles.actionIconWrapper}>
                  <button
                    className={styles.backButton}
                    onClick={closeActiveTool}
                  >
                    <ArrowLeft size="md" />
                  </button>
                </div>
                {activeTool.component}
              </div>
            ) : (
              <>
                {hasPageTools && (
                  <ToolCollection
                    collection={PAGE_TOOLS}
                    title="On this page"
                    onToolClick={openTool}
                  />
                )}
                {currentUser && (
                  <ToolCollection
                    collection={USER_TOOLS}
                    title="Authenticated user"
                    onToolClick={openTool}
                  />
                )}
                <ToolCollection
                  collection={PRIMARY_TOOLS}
                  title="Tools"
                  onToolClick={openTool}
                />
                <Caption
                  variant="lg-regular"
                  className={styles.versionInformation}
                >
                  Version: <VersionTag /> | Env:{' '}
                  {process.env.NEXT_PUBLIC_DEPLOY_ENV}
                </Caption>
              </>
            )}
          </div>
        )}
      </>
    );
  }

  return null;
}
